import { z } from 'zod';
import { Language } from '../enums.js';
import { TimestampSchema } from '../primitives.js';
import { EmailProvider } from '../contracts/email.js';
/**
 * `businesses/{businessId}/emailNotifications/{emailNotificationId}` — the
 * owner-email ledger (doc 29 D5, doc 87).
 *
 * A SEPARATE collection from the SMS ledger rather than a `channel` field on
 * it, for two reasons that are both about the SMS ledger being right as it is:
 * its `to` is an `E164` (an email address cannot parse as one), and its
 * document id is the idempotency claim for a CLIENT message — sharing the
 * namespace would make an owner email and a client SMS about the same booking
 * compete for one id.
 *
 * Everything else is copied on purpose: the id is deterministic
 * (`{appointmentId}_{kind}`) and creating it IS the claim, because Firestore
 * triggers are at-least-once and a duplicate here is a real email in a real
 * inbox. Suppressed rows are written rather than skipped, so "why did I not get
 * an email about that booking?" has an answer that does not depend on logs.
 *
 * Server-written entirely; owners read, never write.
 */
export const EmailNotificationKind = z.enum([
    /** A caller booked (doc 29). */
    'owner_booked',
    /** A caller moved an existing booking (doc 87). */
    'owner_moved',
    /** A caller cancelled (doc 87). */
    'owner_cancelled',
]);
/** Why nothing was sent. */
export const EmailSuppressReason = z.enum([
    /** The business has owner email switched off. */
    'disabled',
    /** Switched on with no address filled in. */
    'no_address',
    /** The stored address is not addressable — a typo we will not send into. */
    'invalid_address',
    /** Owner test call (doc 13a §6): a demo booking is not news about the business. */
    'test_appointment',
    /** No email vendor is configured on this deployment (doc 29 §7). */
    'no_provider',
    /** The owner made this change themselves — from the portal — so it is not
     *  news to them (doc 43 D10, doc 87). */
    'own_action',
]);
export const EmailNotificationStatus = z.enum(['claimed', 'sent', 'failed', 'suppressed']);
export const EmailNotificationSchema = z.object({
    kind: EmailNotificationKind,
    appointmentId: z.string().min(1),
    /** Empty on a `no_address` suppression — there was nobody to address. */
    to: z.string().max(254).default(''),
    language: Language,
    /** Null until a vendor is resolved (i.e. on every suppression). */
    provider: EmailProvider.nullable().default(null),
    status: EmailNotificationStatus,
    suppressReason: EmailSuppressReason.nullable().default(null),
    /** The vendor's own id, for correlating bounces and support cases. */
    providerMessageId: z.string().nullable().default(null),
    error: z.string().max(500).nullable().default(null),
    claimedAt: TimestampSchema,
    sentAt: TimestampSchema.optional(),
});
export const EmailNotificationWithIdSchema = EmailNotificationSchema.extend({ id: z.string() });
/** The ledger document id — deterministic, and therefore the claim (D5). */
export function emailNotificationId(appointmentId, kind) {
    return `${appointmentId}_${kind}`;
}
//# sourceMappingURL=emailNotification.js.map